import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Flex } from '$components/layouts';
import { isDefined, mq } from '$lib/helpers';
import { PersonRoles } from '~/constants';
import { IEnrichedProduct, IEnrichedProductPerson } from '$models';
import { useTranslation } from '$hooks';
import { key } from '~/utils';
import { Link } from '$components/elements/link';
import { SvgIcon } from '$components/elements/svg-icon';
import { theme } from '$theme';
import { Text } from '$components/elements/text';

type ProductDetailsProps = {
    variant: IEnrichedProduct;
    simple?: boolean;
    onReviewClick?: () => void;
};

export const ProductHeader: FC<ProductDetailsProps> = ({
    variant,
    simple,
    onReviewClick,
}: ProductDetailsProps): JSX.Element => {
    const { product } = variant;
    const { translate } = useTranslation();
    const [authors, setAuthors] = useState<IEnrichedProductPerson[]>([]);

    useEffect(() => {
        setAuthors(
            variant?.persons?.filter((person: IEnrichedProductPerson) =>
                person?.personTypes?.some((personType) => personType === PersonRoles.Author)
            ) || []
        );
    }, [variant]);

    const getFlagIcon = (language?: string[]) => {
        if (!language) {
            return <></>;
        }
        const lang = Array.isArray(language) ? language[0] : language;
        switch (lang.toLowerCase()) {
            case 'dansk':
            case 'danish':
            case 'dk':
                return <SvgIcon svg="dk" size={16} />;
            case 'english':
            case 'eng':
            case 'engelsk':
            case 'gb':
                return <SvgIcon svg="gb" size={16} />;
            case 'norsk':
            case 'norwegian':
            case 'no':
            case 'bokmål':
            case 'nynorsk':
                return <SvgIcon svg="no" size={16} />;
            case 'svenska':
            case 'svensk':
            case 'se':
                return <SvgIcon svg="se" size={16} />;
            case 'tysk':
            case 'de':
                return <SvgIcon svg="de" size={16} />;
            case 'finsk':
            case 'fi':
                return <SvgIcon svg="fi" size={16} />;
            case 'spain':
            case 'spansk':
            case 'es':
                return <SvgIcon svg="es" size={16} />;
            case 'polsk':
            case 'pl':
                return <SvgIcon svg="pl" size={16} />;
            case 'fransk':
            case 'fr':
                return <SvgIcon svg="fr" size={16} />;
            case 'hollandsk':
            case 'nl':
                return <SvgIcon svg="nl" size={16} />;
            case 'italiensk':
            case 'it':
                return <SvgIcon svg="it" size={16} />;
            default:
                return <></>;
        }
    };

    return (
        <>
            <ProductHeaderContainer>
                <ProductHeaderTitle>{product?.itemName}</ProductHeaderTitle>
                {product?.itemUnderTitle && (
                    <ProductHeaderUnderTitle>{product?.itemUnderTitle}</ProductHeaderUnderTitle>
                )}
            </ProductHeaderContainer>
            {!simple && (
                <ProductInfoContainer>
                    <>
                        {product?.isBook ? (
                            <>
                                {!!authors?.filter((author) => !!author.personName).length && (
                                    <StyledLinks>
                                        {`${translate('productPage.productDetails.authorPrefix')} `}
                                        {authors
                                            .filter((a) => isDefined(a.personName))
                                            .slice(0, 1)
                                            .map((author) => (
                                                <Link key={key(author)} themedLink href={author.personUrl}>
                                                    {author?.personName}
                                                </Link>
                                            ))}
                                    </StyledLinks>
                                )}
                            </>
                        ) : (
                            <>
                                {!product?.isBook && !!product?.productBrand && (
                                    <StyledLinks>
                                        <span>{translate('productPage.productDetails.productBrandPrefix')}</span>
                                        {product.productBrand}
                                    </StyledLinks>
                                )}
                            </>
                        )}
                    </>

                    <Flex>
                        <SvgIcon svg="starFull" color={theme.colors.red500} size={13} />
                        <ReviewScore size="14px">
                            {(((product?.itemReviewScore || 0) / 100) * 5).toFixed(1)}
                        </ReviewScore>
                        <ReviewsLink onClick={onReviewClick}>{`( ${
                            product?.itemTotalNumberOfReviews || 0
                        } )`}</ReviewsLink>
                    </Flex>
                    {product?.itemLanguage && (
                        <Flex alignItems="center" gap="extrasmall">
                            {getFlagIcon(product.itemLanguage)}
                            <ProductLang size="14px">{product.itemLanguage}</ProductLang>
                        </Flex>
                    )}
                </ProductInfoContainer>
            )}
        </>
    );
};

const StyledLinks = styled.h2(({ theme }) => ({
    display: 'flex',
    gap: '2px',
    alignItems: 'center',
    fontSize: theme.fontSizes.sm,
    fontFamily: theme.fontFamilies.regular,
    fontWeight: theme.fontWeights.normal,
    marginRight: 0,
    marginBottom: 0,
    maxHeight: '21px',
}));

const ReviewsLink = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes.sm,
    fontFamily: theme.fontFamilies.regular,
    color: theme.colors.textSubtle,
    cursor: 'pointer',

    '&:hover': {
        textDecoration: 'underLine',
    },
}));

const ProductHeaderContainer = styled.div(({ theme }) => ({
    marginBottom: theme.space[6],
}));

const ProductHeaderTitle = styled.h1(({ theme }) => ({
    // fontSize: '36px',
    fontFamily: theme.fontFamilies.regular,
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.space[2],
    lineHeight: theme.lineHeights.single,
}));

const ProductHeaderUnderTitle = styled.div(({ theme }) => ({
    fontStyle: 'italic',
    fontSize: theme.fontSizes.md,
    margin: 0,
    color: theme.colors.grey80,
}));

const ProductInfoContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[1],
    [mq('frame')]: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.space[1],
        [`& > :not(:last-child):after`]: {
            display: 'block',
            width: '1px',
            height: '20px',
            backgroundColor: theme.colors.borderColor,
            content: '""',
            margin: `0 ${theme.space[4]}`,
        },
    },
}));

const ReviewScore = styled(Text)(({ theme }) => ({
    margin: `0 ${theme.space[1]}`,
}));

const ProductLang = styled(Text)(() => ({}));
